import type { LngLat, Route } from '../core/types';
import { distance, nearestPointOnSegment } from '../core/geo';

const CELL = 0.0015;
const SAMPLE_M = 100;
type Trace = { points: LngLat[]; segments: Array<[LngLat, LngLat]>; grid: Map<string, Set<number>> };
const traces = new WeakMap<Route, Trace>();

function trace(route: Route): Trace {
  const cached = traces.get(route);
  if (cached) return cached;
  const coordinates = route.geometry.coordinates;
  const points: LngLat[] = [];
  const segments: Array<[LngLat, LngLat]> = [];
  const grid = new Map<string, Set<number>>();
  for (let i = 1; i < coordinates.length; i++) {
    const a = coordinates[i - 1], b = coordinates[i];
    const segment = segments.push([a, b]) - 1;
    const count = Math.max(1, Math.ceil(distance(a, b) / SAMPLE_M));
    for (let j = 0; j <= count; j++) {
      const p: LngLat = [a[0] + (b[0] - a[0]) * j / count, a[1] + (b[1] - a[1]) * j / count];
      if (j < count) points.push(p);
      const key = `${Math.floor(p[0] / CELL)}:${Math.floor(p[1] / CELL)}`;
      const bucket = grid.get(key) ?? new Set<number>();
      bucket.add(segment); grid.set(key, bucket);
    }
  }
  if (coordinates.length) points.push(coordinates[coordinates.length - 1]);
  const result = { points, segments, grid };
  traces.set(route, result);
  return result;
}

function follows(a: Trace, b: Trace): boolean {
  let apart = 0, totalApart = 0;
  for (let i = 0; i < a.points.length; i++) {
    const p = a.points[i];
    const x = Math.floor(p[0] / CELL), y = Math.floor(p[1] / CELL);
    let near = false;
    for (let dx = -1; dx <= 1 && !near; dx++) {
      for (let dy = -1; dy <= 1 && !near; dy++) {
        for (const index of b.grid.get(`${x + dx}:${y + dy}`) ?? []) {
          const [start, end] = b.segments[index];
          if (nearestPointOnSegment(p, start, end).distance <= 25) { near = true; break; }
        }
      }
    }
    const length = i ? distance(a.points[i - 1], p) : 0;
    apart = near ? 0 : apart + length;
    if (!near) totalApart += length;
    // Keep a genuine different ramp/road, even on a 400 km shared journey.
    if (apart >= 350 || totalApart >= 700) return false;
  }
  return true;
}

/** Geometry wins over inconsistent road classes, traffic times and point density. */
export function sameRoutePath(a: Route, b: Route): boolean {
  if (a.geometry.coordinates.length < 2 || b.geometry.coordinates.length < 2) return false;
  const first = trace(a), second = trace(b);
  if (distance(first.points[0], second.points[0]) > 100 || distance(first.points.at(-1)!, second.points.at(-1)!) > 100) return false;
  return follows(first, second) && follows(second, first);
}
